import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { ArrowLeft, ChevronDown, ChevronRight, RotateCcw, Send, Check, X } from 'lucide-react';

const ACCENT = '#f97316';

interface WebhookEndpoint {
  id: string;
  name: string;
  url: string;
  events: string[];
  status: 'active' | 'paused' | 'disabled';
  consecutive_failures: number;
  failure_threshold: number;
  last_delivery_at: string | null;
  created_at: string;
  updated_at: string;
  headers: Record<string, string>;
  retry_policy: { max_retries: number; backoff_multiplier: number; initial_delay_seconds: number };
}

interface WebhookDelivery {
  id: string;
  event_type: string;
  status: 'pending' | 'delivering' | 'delivered' | 'retrying' | 'failed';
  attempt_count: number;
  response_status: number | null;
  response_body: string | null;
  response_time_ms: number | null;
  error_message: string | null;
  created_at: string;
  delivered_at: string | null;
  payload: object;
}

interface Stats {
  last_24h: { total: number; delivered: number; failed: number; success_rate: number; avg_response_ms: number | null };
  last_7d: { total: number; delivered: number; failed: number; success_rate: number; avg_response_ms: number | null };
  last_30d: { total: number; delivered: number; failed: number; success_rate: number; avg_response_ms: number | null };
}

type Tab = 'deliveries' | 'events' | 'settings' | 'debug';

export default function WebhookDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [webhook, setWebhook] = useState<WebhookEndpoint | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [debugDeliveries, setDebugDeliveries] = useState<WebhookDelivery[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('deliveries');
  const [selectedDelivery, setSelectedDelivery] = useState<WebhookDelivery | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; status_code?: number; response_time_ms?: number } | null>(null);

  // Edit settings
  const [editName, setEditName] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [whRes, delRes, statsRes] = await Promise.all([
        api.get<{ webhook: WebhookEndpoint }>(`/webhooks/${id}`),
        api.get<{ deliveries: WebhookDelivery[] }>(`/webhooks/${id}/deliveries`),
        api.get<{ stats: Stats }>(`/webhooks/${id}/stats`),
      ]);
      setWebhook(whRes.webhook);
      setDeliveries(delRes.deliveries);
      setStats(statsRes.stats);
      setEditName(whRes.webhook.name);
      setEditUrl(whRes.webhook.url);
    } catch { /* empty */ }
    setLoading(false);
  }, [id]);

  const loadDebug = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get<{ failed_deliveries: WebhookDelivery[] }>(`/webhooks/${id}/debug`);
      setDebugDeliveries(res.failed_deliveries);
    } catch { /* empty */ }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (activeTab === 'debug') loadDebug(); }, [activeTab, loadDebug]);

  const handleRetry = async (deliveryId: string) => {
    if (!id) return;
    setRetrying(deliveryId);
    try {
      await api.post(`/webhooks/${id}/deliveries/${deliveryId}/retry`, {});
      load();
    } catch { /* empty */ }
    setRetrying(null);
  };

  const handleTest = async () => {
    if (!id) return;
    setTesting(true);
    try {
      const res = await api.post<{ success: boolean; status_code?: number; response_time_ms?: number }>(`/webhooks/${id}/test`);
      setTestResult(res);
      setTimeout(() => setTestResult(null), 5000);
      load();
    } catch { /* empty */ }
    setTesting(false);
  };

  const handleSave = async () => {
    if (!id || !webhook) return;
    setSaving(true);
    try {
      await api.patch(`/webhooks/${id}`, { name: editName.trim(), url: editUrl.trim() });
      load();
    } catch (err: any) {
      alert(err.message || 'Failed to save');
    }
    setSaving(false);
  };

  const statusColor = (s: WebhookDelivery['status']) =>
    s === 'delivered' ? { bg: '#dcfce7', color: '#166534' } :
    s === 'failed' ? { bg: '#fee2e2', color: '#991b1b' } :
    s === 'retrying' ? { bg: '#fef9c3', color: '#854d0e' } :
    { bg: '#f3f4f6', color: '#6b7280' };

  if (loading) return <p style={{ color: '#6b7280' }}>Loading...</p>;
  if (!webhook) return <p style={{ color: '#6b7280' }}>Webhook not found</p>;

  const epStatusColor = webhook.status === 'active' ? '#16a34a' : webhook.status === 'paused' ? '#ca8a04' : '#dc2626';

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button onClick={() => navigate('/webhooks')} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.875rem', padding: 0, marginBottom: '0.75rem' }}>
          <ArrowLeft size={16} /> Back to Webhooks
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>{webhook.name}</h1>
            <p style={{ fontFamily: 'monospace', fontSize: '0.875rem', color: '#6b7280', margin: '0.25rem 0 0' }}>{webhook.url}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: epStatusColor, display: 'inline-block' }} />
            <span style={{ fontSize: '0.875rem', fontWeight: 500, color: epStatusColor }}>{webhook.status}</span>
          </div>
          <button onClick={handleTest} disabled={testing} style={btnStyle}>
            {testing ? 'Sending...' : <><Send size={14} /> Test</>}
          </button>
        </div>
        {testResult && (
          <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: testResult.success ? '#f0fdf4' : '#fef2f2', borderRadius: '0.375rem', border: `1px solid ${testResult.success ? '#bbf7d0' : '#fecaca'}` }}>
            <span style={{ fontWeight: 500, color: testResult.success ? '#16a34a' : '#dc2626' }}>
              {testResult.success ? <Check size={14} style={{ verticalAlign: 'middle', marginRight: '0.25rem' }} /> : <X size={14} style={{ verticalAlign: 'middle', marginRight: '0.25rem' }} />}
              {testResult.success ? 'Test delivered successfully' : 'Test delivery failed'}
            </span>
            {testResult.status_code && <span style={{ color: '#6b7280', fontSize: '0.75rem', marginLeft: '0.5rem' }}>HTTP {testResult.status_code}</span>}
            {testResult.response_time_ms && <span style={{ color: '#6b7280', fontSize: '0.75rem', marginLeft: '0.5rem' }}>{testResult.response_time_ms}ms</span>}
          </div>
        )}
      </div>

      {/* Stats bar */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
          {(['last_24h', 'last_7d', 'last_30d'] as const).map(period => {
            const s = stats[period];
            const label = period === 'last_24h' ? 'Last 24h' : period === 'last_7d' ? 'Last 7 days' : 'Last 30 days';
            return (
              <div key={period} style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1rem' }}>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.5rem', fontWeight: 500 }}>{label}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <div>
                    <p style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0, color: s.success_rate >= 95 ? '#16a34a' : s.success_rate >= 80 ? '#ca8a04' : '#dc2626' }}>{s.success_rate}%</p>
                    <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.125rem 0 0' }}>{s.delivered}/{s.total} delivered</p>
                  </div>
                  {s.avg_response_ms != null && (
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>{s.avg_response_ms}ms avg</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', marginBottom: '1.5rem', gap: '0' }}>
        {(['deliveries', 'events', 'settings', 'debug'] as Tab[]).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.625rem 1.25rem', fontSize: '0.875rem', fontWeight: 500,
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: activeTab === tab ? `2px solid ${ACCENT}` : '2px solid transparent',
              color: activeTab === tab ? ACCENT : '#6b7280',
              marginBottom: '-2px', textTransform: 'capitalize',
            }}
          >{tab}</button>
        ))}
      </div>

      {/* Deliveries Tab */}
      {activeTab === 'deliveries' && (
        <div>
          {deliveries.length === 0 ? (
            <p style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem' }}>No deliveries yet</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {deliveries.map(d => {
                const sc = statusColor(d.status);
                const isSelected = selectedDelivery?.id === d.id;
                return (
                  <div key={d.id} style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: `1px solid ${isSelected ? ACCENT : '#e5e7eb'}` }}>
                    <button
                      onClick={() => setSelectedDelivery(isSelected ? null : d)}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                    >
                      {isSelected ? <ChevronDown size={16} color="#9ca3af" /> : <ChevronRight size={16} color="#9ca3af" />}
                      <span style={{ padding: '0.125rem 0.5rem', fontSize: '0.6875rem', borderRadius: '9999px', backgroundColor: sc.bg, color: sc.color, fontWeight: 500, flexShrink: 0 }}>{d.status}</span>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#374151', flex: 1 }}>{d.event_type}</span>
                      {d.response_status && <span style={{ fontSize: '0.75rem', color: d.response_status >= 200 && d.response_status < 300 ? '#16a34a' : '#dc2626' }}>HTTP {d.response_status}</span>}
                      {d.response_time_ms && <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{d.response_time_ms}ms</span>}
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>{new Date(d.created_at).toLocaleString()}</span>
                    </button>

                    {isSelected && (
                      <div style={{ padding: '0 1rem 1rem', borderTop: '1px solid #f3f4f6' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                          <div>
                            <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', margin: '0 0 0.5rem' }}>Request Payload</p>
                            <pre style={{ fontSize: '0.6875rem', color: '#374151', backgroundColor: '#f9fafb', padding: '0.75rem', borderRadius: '0.375rem', overflow: 'auto', maxHeight: 200, margin: 0 }}>
                              {JSON.stringify(d.payload, null, 2)}
                            </pre>
                          </div>
                          <div>
                            <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', margin: '0 0 0.5rem' }}>Response</p>
                            {d.response_body ? (
                              <pre style={{ fontSize: '0.6875rem', color: '#374151', backgroundColor: '#f9fafb', padding: '0.75rem', borderRadius: '0.375rem', overflow: 'auto', maxHeight: 200, margin: 0 }}>
                                {d.response_body}
                              </pre>
                            ) : (
                              <p style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{d.error_message || 'No response'}</p>
                            )}
                          </div>
                        </div>

                        {/* Timeline */}
                        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                          <span>Created: {new Date(d.created_at).toLocaleString()}</span>
                          {d.delivered_at && <span>Delivered: {new Date(d.delivered_at).toLocaleString()}</span>}
                          <span>Attempts: {d.attempt_count}</span>
                        </div>

                        {d.status === 'failed' && (
                          <button
                            onClick={() => handleRetry(d.id)}
                            disabled={retrying === d.id}
                            style={{ ...btnStyle, marginTop: '0.75rem', fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                          >
                            <RotateCcw size={12} /> {retrying === d.id ? 'Retrying...' : 'Retry'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Events Tab */}
      {activeTab === 'events' && (
        <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem' }}>
          <p style={{ fontWeight: 600, margin: '0 0 1rem' }}>Subscribed Events ({webhook.events.length})</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {webhook.events.map(event => (
              <div key={event} style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0.75rem', backgroundColor: '#f9fafb', borderRadius: '0.375rem' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.875rem', flex: 1 }}>{event}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem', maxWidth: 480 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={labelStyle}>Name</label>
              <input value={editName} onChange={e => setEditName(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>URL</label>
              <input value={editUrl} onChange={e => setEditUrl(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Retry Policy</label>
              <div style={{ backgroundColor: '#f9fafb', borderRadius: '0.375rem', padding: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
                <p style={{ margin: '0 0 0.25rem' }}>Max retries: {webhook.retry_policy.max_retries}</p>
                <p style={{ margin: '0 0 0.25rem' }}>Backoff: ×{webhook.retry_policy.backoff_multiplier}</p>
                <p style={{ margin: 0 }}>Initial delay: {webhook.retry_policy.initial_delay_seconds}s</p>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Failure Threshold</label>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>Auto-disabled after {webhook.failure_threshold} consecutive failures (current: {webhook.consecutive_failures})</p>
            </div>
            {Object.keys(webhook.headers).length > 0 && (
              <div>
                <label style={labelStyle}>Custom Headers</label>
                <div style={{ backgroundColor: '#f9fafb', borderRadius: '0.375rem', padding: '0.75rem' }}>
                  {Object.entries(webhook.headers).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', gap: '0.5rem', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                      <span style={{ fontFamily: 'monospace', color: '#374151' }}>{k}:</span>
                      <span style={{ color: '#6b7280' }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.5rem' }}>
            <button onClick={handleSave} disabled={saving} style={{ ...btnStyle, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Debug Tab */}
      {activeTab === 'debug' && (
        <div>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>Recent failed deliveries with full request/response details.</p>
          {debugDeliveries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
              <Check size={24} color="#16a34a" style={{ margin: '0 auto 0.5rem' }} />
              <p style={{ color: '#6b7280', margin: 0 }}>No recent failures</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {debugDeliveries.map(d => (
                <div key={d.id} style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #fecaca', padding: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <div>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 600 }}>{d.event_type}</span>
                      {d.response_status && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#dc2626' }}>HTTP {d.response_status}</span>}
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{new Date(d.created_at).toLocaleString()}</span>
                  </div>
                  {d.error_message && <p style={{ fontSize: '0.75rem', color: '#dc2626', margin: '0 0 0.75rem', backgroundColor: '#fef2f2', padding: '0.5rem', borderRadius: '0.25rem' }}>{d.error_message}</p>}
                  {d.response_body && (
                    <div>
                      <p style={{ fontSize: '0.75rem', fontWeight: 500, color: '#374151', margin: '0 0 0.25rem' }}>Response Body</p>
                      <pre style={{ fontSize: '0.6875rem', backgroundColor: '#f9fafb', padding: '0.5rem', borderRadius: '0.25rem', overflow: 'auto', maxHeight: 150, margin: '0 0 0.75rem' }}>{d.response_body}</pre>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                    <span>Attempt {d.attempt_count}</span>
                    {d.response_time_ms && <span>{d.response_time_ms}ms</span>}
                  </div>
                  <button
                    onClick={() => handleRetry(d.id)}
                    disabled={retrying === d.id}
                    style={{ ...btnStyle, marginTop: '0.75rem', fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                  >
                    <RotateCcw size={12} /> {retrying === d.id ? 'Retrying...' : 'Retry'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const btnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
  padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500,
  backgroundColor: ACCENT, color: '#fff', border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.75rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem',
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '0.5rem', fontSize: '0.875rem',
  border: '1px solid #d1d5db', borderRadius: '0.375rem', boxSizing: 'border-box',
};
